import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { a as anime } from './anime.es.js';
import { c as createTypeValidator, a as createLogger, e as emitEvent, f as isUnset, d as isNumber, l as logDeprecatedEventOnce } from './utils.js';

// Constants
const component = 'cl-animation';
const animationConstants = {
  DIRECTION: {
    REVERSE: 'reverse',
    ALTERNATE: 'alternate',
    NORMAL: 'normal',
  },
  EASING: {
    LINEAR: 'linear',
    EASE_IN_QUAD: 'easeInQuad',
    EASE_IN_CUBIC: 'easeInCubic',
    EASE_IN_QUART: 'easeInQuart',
    EASE_IN_QUINT: 'easeInQuint',
    EASE_IN_SINE: 'easeInSine',
    EASE_IN_EXPO: 'easeInExpo',
    EASE_IN_CIRC: 'easeInCirc',
    EASE_IN_BACK: 'easeInBack',
    EASE_OUT_QUAD: 'easeOutQuad',
    EASE_OUT_CUBIC: 'easeOutCubic',
    EASE_OUT_QUART: 'easeOutQuart',
    EASE_OUT_QUINT: 'easeOutQuint',
    EASE_OUT_SINE: 'easeOutSine',
    EASE_OUT_EXPO: 'easeOutExpo',
    EASE_OUT_CIRC: 'easeOutCirc',
    EASE_OUT_BACK: 'easeOutBack',
    EASE_IN_BOUNCE: 'easeInBounce',
    EASE_IN_OUT_QUAD: 'easeInOutQuad',
    EASE_IN_OUT_CUBIC: 'easeInOutCubic',
    EASE_IN_OUT_QUART: 'easeInOutQuart',
    EASE_IN_OUT_QUINT: 'easeInOutQuint',
    EASE_IN_OUT_SINE: 'easeInOutSine',
    EASE_IN_OUT_EXPO: 'easeInOutExpo',
    EASE_IN_OUT_CIRC: 'easeInOutCirc',
    EASE_IN_OUT_BACK: 'easeInOutBack',
    EASE_IN_OUT_BOUNCE: 'easeInOutBounce',
    EASE_OUT_BOUNCE: 'easeOutBounce',
    EASE_OUT_IN_QUAD: 'easeOutInQuad',
    EASE_OUT_IN_CUBIC: 'easeOutInCubic',
    EASE_OUT_IN_QUART: 'easeOutInQuart',
    EASE_OUT_IN_QUINT: 'easeOutInQuint',
    EASE_OUT_IN_SINE: 'easeOutInSine',
    EASE_OUT_IN_EXPO: 'easeOutInExpo',
    EASE_OUT_IN_CIRC: 'easeOutInCirc',
    EASE_OUT_IN_BACK: 'easeOutInBack',
    EASE_OUT_IN_BOUNCE: 'easeOutInBounce',
  },
};
// Type-validators
const animationValidator = {
  easing: createTypeValidator(animationConstants.EASING),
  direction: createTypeValidator(animationConstants.DIRECTION),
};

const ClAnimation = /*@__PURE__*/ proxyCustomElement(class ClAnimation extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.clAnimationEnd = createEvent(this, "clAnimationEnd", 7);
    this.clAnimationStart = createEvent(this, "clAnimationStart", 7);
    this.animationEnd = createEvent(this, "endAnimation", 7);
    this.animationStart = createEvent(this, "startAnimation", 7);
    /**
     * Object containing custom consoleLogger functions.
     */
    this.logger = createLogger(component);
    /**
     * Whether the accordion is being animated or not
     */
    this.isAnimating = false;
    /**
     * Validates and sets the animation function
     */
    this.handleAnimate = () => {
      return this.play ? this.animation().play() : this.animation();
    };
    /**
     * Handles emitting the animation start event when the animation begins
     */
    this.handleAnimationBegin = (event) => {
      if (!this.isAnimating) {
        this.isAnimating = true;
        emitEvent(this, 'clAnimationStart', { component, originalEvent: event }, null);
      }
    };
    /**
     * Handles emitting the animation end event when the animation completed
     */
    this.handleAnimationComplete = (event) => {
      if (this.isAnimating) {
        this.isAnimating = false;
        emitEvent(this, 'clAnimationEnd', { component, originalEvent: event }, null);
      }
    };
    /**
     * Function that handles all properties within a library built-in anime()
     */
    this.animation = () => {
      const animateItem = this.singleItemWrapper;
      const animateList = this.element.children;
      const { delay, direction, duration, easing, fade, loop, movement, rotate, scale, shrink, stagger } = this;
      const [rotateX = 0, rotateY = 0, rotateZ = 0] = this.rotateValues;
      const [translateX = 0, translateY = 0, translateZ = 0] = this.movementValues;
      return anime(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, (stagger
        ? { targets: animateList, delay: anime.stagger(stagger, { start: delay, easing }) }
        : { targets: animateItem, delay, easing })), { autoplay: false }), (direction && { direction })), (duration && { duration })), (fade && { opacity: fade ? [0, 1] : null })), (loop && { loop })), (movement && {
        translateX: `${translateX}rem`,
        translateY: `${translateY}rem`,
        translateZ: `${translateZ}rem`,
      })), (rotate && { rotateX, rotateY, rotateZ })), ((scale || shrink) && { scale: shrink ? 0 : scale })), this.configValues), { begin: this.handleAnimationBegin, complete: this.handleAnimationComplete }));
    };
    this.configValues = undefined;
    this.movementValues = [];
    this.rotateValues = [];
    this.config = undefined;
    this.delay = 0;
    this.direction = 'normal';
    this.duration = 0;
    this.easing = 'linear';
    this.fade = undefined;
    this.loop = false;
    this.movement = undefined;
    this.play = false;
    this.rotate = undefined;
    this.scale = undefined;
    this.shrink = undefined;
    this.stagger = undefined;
  }
  /**
   * Parses and validates the given config
   */
  parseAndValidateConfig(config) {
    if (!isUnset(config)) {
      try {
        if (config) {
          const parsedValue = JSON.parse(config);
          this.configValues = parsedValue;
        }
      }
      catch (error) {
        this.logger.error.attribute.config(`Given invalid value "${config}".`, 'Please put in a valid JSON object.');
      }
    }
  }
  /**
   * Validates the delay property
   */
  handleDelay(delay) {
    if (!isNumber(delay)) {
      this.logger.error.attribute.delay(`Given invalid value "${delay}".`);
    }
  }
  /**
   * Validates the direction property
   */
  handleDirection(direction) {
    if (!animationValidator.direction(direction)) {
      this.logger.error.attribute.direction(`Given invalid value "${direction}".`);
    }
    if (this.stagger && direction === 'alternate') {
      this.logger.warn.attribute.direction("Please use 'normal' or 'reverse' in combination with property 'stagger'.");
    }
  }
  /**
   * Validates the duration property
   */
  handleDuration(duration) {
    if (!isNumber(duration)) {
      this.logger.error.attribute.duration(`Given invalid value "${duration}".`);
    }
  }
  /**
   * Validates the easing property
   */
  handleEasing(easing) {
    if (!animationValidator.easing(easing)) {
      this.logger.error.attribute.easing(`Given invalid value "${easing}".`);
    }
  }
  /**
   * Parses and validates the given movement values
   */
  parseAndValidateMovement(movement) {
    if (!isUnset(movement)) {
      try {
        if (movement) {
          const parsedValue = JSON.parse(movement);
          this.movementValues = parsedValue;
        }
      }
      catch (error) {
        this.logger.error.attribute.movement(`Given invalid value "${movement}".`, 'Please put in a valid JSON array.');
      }
    }
  }
  /**
   * Parses and validates the given rotate values
   */
  parseAndValidateRotate(rotate) {
    if (!isUnset(rotate)) {
      try {
        if (rotate) {
          const parsedValue = JSON.parse(rotate);
          this.rotateValues = parsedValue;
        }
      }
      catch (error) {
        this.logger.error.attribute.rotate(`Given invalid value "${rotate}".`, 'Please put in a valid JSON array.');
      }
    }
  }
  /**
   * Validates the scale property
   */
  handleScale(scale) {
    if (!isUnset(scale) && !isNumber(scale)) {
      this.logger.error.attribute.scale(`Given invalid value "${scale}".`);
    }
  }
  /**
   * Validates the stagger property
   */
  handleStagger(stagger) {
    if (!isUnset(stagger) && !isNumber(stagger)) {
      this.logger.error.attribute.stagger(`Given invalid value "${stagger}".`);
    }
  }
  /**
   * Every time the component is connected to the DOM:
   * - Console log deprecation warnings;
   * - Validate initial attributes;
   */
  connectedCallback() {
    logDeprecatedEventOnce(this.logger, 'endAnimation', 'clAnimationEnd');
    logDeprecatedEventOnce(this.logger, 'startAnimation', 'clAnimationStart');
    this.handleDelay(this.delay);
    this.handleDirection(this.direction);
    this.handleDuration(this.duration);
    this.handleEasing(this.easing);
    this.handleScale(this.scale);
    this.handleStagger(this.stagger);
  }
  /**
   * Called after every render()
   */
  componentDidRender() {
    this.handleAnimate();
  }
  /**
   * Called once just after the component is first connected to the DOM
   */
  componentWillLoad() {
    this.parseAndValidateConfig(this.config);
    this.parseAndValidateRotate(this.rotate);
    this.parseAndValidateMovement(this.movement);
  }
  render() {
    const { stagger } = this;
    return stagger ? (h("slot", { name: 'list' })) : (h("div", { ref: el => {
        this.singleItemWrapper = el;
      } }, h("slot", { name: 'item' })));
  }
  get element() { return this; }
  static get watchers() { return {
    "config": ["parseAndValidateConfig"],
    "delay": ["handleDelay"],
    "direction": ["handleDirection"],
    "duration": ["handleDuration"],
    "easing": ["handleEasing"],
    "movement": ["parseAndValidateMovement"],
    "rotate": ["parseAndValidateRotate"],
    "scale": ["handleScale"],
    "stagger": ["handleStagger"]
  }; }
}, [6, "cl-animation", {
    "config": [1],
    "delay": [2],
    "direction": [1],
    "duration": [2],
    "easing": [1],
    "fade": [4],
    "loop": [4],
    "movement": [1],
    "play": [4],
    "rotate": [1],
    "scale": [2],
    "shrink": [4],
    "stagger": [2],
    "configValues": [32],
    "movementValues": [32],
    "rotateValues": [32]
  }]);
function defineCustomElement() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["cl-animation"];
  components.forEach(tagName => { switch (tagName) {
    case "cl-animation":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ClAnimation);
      }
      break;
  } });
}

export { ClAnimation as C, defineCustomElement as d };

//# sourceMappingURL=cl-animation2.js.map